
import { Stack } from '@mui/system';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { MenuItem, IconButton, Typography } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { ReferenceOpenStatus, ReferenceVisibilityStatus, ApplicationReferenceResponse } from 'src/services/references/references.types';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';


// ----------------------------------------------------------------------

type Props = {
  row: ApplicationReferenceResponse;
  showVisibilityStatus?: boolean;
  onViewSubmissions: (referenceId: string) => void;
  onShareWithClient?: (referenceId: string) => void;
};

export default function ApplicationReferencesTableRow({
  row,
  showVisibilityStatus = false,
  onViewSubmissions,
  onShareWithClient
}: Props) {

  const confirm = useBoolean();

  const showActions = usePopover();

  const { t } = useTranslate();

  const tenantType = useOrgTenant();

  return (
    <TableRow hover>
      <TableCell width={400}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2">
            {row.referee.first_name} {row.referee.last_name}
          </Typography>

          {
            row.open_status === ReferenceOpenStatus.NOT_VIEWED && (
              <Label color='primary'>{t('common.new')}</Label>
            )
          }

        </Stack>
      </TableCell>
      <TableCell>
        {row.referee.email}
      </TableCell>
      <TableCell>
        {row.referee.phone}
      </TableCell>
      <TableCell>
        {row.referee.company_name}
      </TableCell>

      {
        showVisibilityStatus && (
          <TableCell>
            {
              row.visibility_status === ReferenceVisibilityStatus.SHARED ? (
                <Label color="success">
                  {t('applications.detail.references.shared')}
                </Label>
              ) : (
                <Label color="error">
                  {t('applications.detail.references.not_shared')}
                </Label>
              )
            }
          </TableCell>
        )
      }

      <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
        <IconButton color={showActions.open ? 'inherit' : 'default'} onClick={showActions.onOpen}>
          <Iconify icon="eva:more-horizontal-fill" />
        </IconButton>
      </TableCell>


      <CustomPopover open={showActions.open} onClose={showActions.onClose} arrow="top-right">

        <MenuItem
          onClick={() => onViewSubmissions(row.id)}
        >
          <Iconify icon="eva:eye-fill" sx={{ mr: 1 }} />
          {t('applications.detail.references.actions.view_submissions')}
        </MenuItem>

        {
          row.visibility_status === ReferenceVisibilityStatus.NOT_SHARED && (
            <MenuItem
              onClick={() => onShareWithClient?.(row.id)}
            >
              <Iconify icon="fluent:share-16-regular" sx={{ mr: 1 }} />
              {t('applications.detail.references.actions.share_with_client')}
            </MenuItem>
          )
        }

      </CustomPopover>

    </TableRow >
  );
}
